import { useMemo, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Phone, Radar, Snowflake, Truck } from "lucide-react";
import { MaskedLine, FadeUp } from "./Reveal";

const PHASES = [
  {
    num: "01",
    icon: Radar,
    title: "Before the storm",
    body: "Route mapping, site walkthroughs, pre-treatment and equipment staged at your lot before the first flake falls.",
  },
  {
    num: "02",
    icon: Snowflake,
    title: "During the storm",
    body: "24/7 on-call dispatch. Plows and sidewalk crews roll through the night so you open on time — every time.",
  },
  {
    num: "03",
    icon: Truck,
    title: "After the storm",
    body: "Cleanup passes, salting and sanding, drift control, and haul-away when the piles get too tall.",
  },
];

const PLANS = [
  { name: "Residential Route", price: "from $55", note: "Driveway + walkway cleared every snowfall" },
  { name: "Commercial Lot", price: "from $549/mo", note: "Lots, lanes & sidewalks with priority dispatch" },
  { name: "Campus / Industrial", price: "Custom", note: "Multi-site contracts with dedicated equipment" },
];

const Snowfall = ({ y }) => {
  const flakes = useMemo(
    () =>
      Array.from({ length: 26 }, (_, i) => ({
        left: (i * 37 + 11) % 100,
        size: 10 + ((i * 7) % 15),
        dur: 9 + ((i * 3) % 11),
        delay: -((i * 5) % 14),
        drift: (i % 2 ? 1 : -1) * (20 + ((i * 11) % 45)),
        op: 0.3 + (((i * 13) % 45) / 100),
      })),
    []
  );
  return (
    <motion.div style={{ y }} className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {flakes.map((f, i) => (
        <Snowflake
          key={i}
          strokeWidth={1.5}
          className="snowfall absolute text-[#9FB6CE]"
          style={{
            left: `${f.left}%`,
            top: "-6%",
            width: f.size,
            height: f.size,
            opacity: f.op,
            "--drift": `${f.drift}px`,
            animationDuration: `${f.dur}s`,
            animationDelay: `${f.delay}s`,
          }}
        />
      ))}
    </motion.div>
  );
};

const SnowPiles = ({ yBack, yFront }) => (
  <>
    <motion.svg
      style={{ y: yBack }}
      viewBox="0 0 1440 180"
      preserveAspectRatio="none"
      className="absolute bottom-0 left-0 w-full h-32 md:h-44 text-[#DCE6F1]"
      aria-hidden="true"
    >
      <path
        fill="currentColor"
        d="M0,120 C240,55 380,145 620,110 C860,75 980,15 1210,70 C1330,98 1390,115 1440,100 L1440,180 L0,180 Z"
      />
    </motion.svg>
    <motion.svg
      style={{ y: yFront }}
      viewBox="0 0 1440 180"
      preserveAspectRatio="none"
      className="absolute bottom-0 left-0 w-full h-28 md:h-36 text-[#EBF1F8]"
      aria-hidden="true"
    >
      <path
        fill="currentColor"
        d="M0,145 C200,95 420,155 720,125 C1020,95 1180,150 1440,115 L1440,180 L0,180 Z"
      />
    </motion.svg>
  </>
);

export const WinterBanner = ({ onNavigate }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const flakeDrift = useTransform(scrollYProgress, [0, 1], [0, -140]);
  const pileBack = useTransform(scrollYProgress, [0, 1], [70, 0]);
  const pileFront = useTransform(scrollYProgress, [0, 1], [120, 0]);

  const reserve = () => {
    window.dispatchEvent(new CustomEvent("prefill-service", { detail: "Snow Removal" }));
    onNavigate("#contact");
  };

  return (
    <section id="winter" ref={ref} className="relative overflow-hidden bg-[#F4F7FA] text-[#050505]" data-testid="winter-banner">
      <Snowfall y={flakeDrift} />
      <div className="relative z-10 max-w-[1800px] mx-auto px-6 sm:px-12 lg:px-24 pt-24 sm:pt-32 pb-44 sm:pb-56">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <FadeUp>
            <p className="font-mono text-xs uppercase tracking-[0.3em] font-bold text-[#FF3B00] flex items-center gap-3">
              <span className="h-2 w-2 bg-[#FF3B00] animate-pulse" />
              Seasonal — Winter 2026/27
            </p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <p className="font-mono text-xs uppercase tracking-[0.3em] font-bold">Route spots limited</p>
          </FadeUp>
        </div>

        <h2 className="font-oswald font-bold uppercase leading-[0.85] tracking-tighter text-[13vw] sm:text-[11vw] lg:text-[9vw]" data-testid="winter-heading">
          <MaskedLine>Snow contracts</MaskedLine>
          <MaskedLine delay={0.12} className="text-outline-dark">Now booking.</MaskedLine>
        </h2>

        <FadeUp delay={0.2}>
          <p className="mt-8 max-w-2xl text-base sm:text-lg font-medium leading-relaxed text-[#050505]/70">
            Lock in a seasonal contract before the first storm and your lot jumps the queue —
            our contract clients get plowed first, every snowfall, all winter.
          </p>
        </FadeUp>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#050505]/15 border border-[#050505]/15">
          {PHASES.map((p, i) => (
            <FadeUp key={p.num} delay={0.08 * i} className="bg-[#F4F7FA]">
              <div className="p-8 sm:p-10 h-full" data-testid={`winter-phase-${p.num}`}>
                <div className="flex items-center justify-between mb-8">
                  <p.icon className="h-7 w-7" strokeWidth={1.5} />
                  <span className="font-mono text-sm font-bold text-[#FF3B00]">{p.num}</span>
                </div>
                <h3 className="font-oswald font-bold uppercase text-2xl sm:text-3xl tracking-tight mb-4">{p.title}</h3>
                <p className="text-sm sm:text-base font-medium leading-relaxed text-[#050505]/65">{p.body}</p>
              </div>
            </FadeUp>
          ))}
        </div>

        <div className="mt-16 border-t-2 border-[#050505]">
          {PLANS.map((p, i) => (
            <FadeUp key={p.name} delay={0.06 * i}>
              <div
                className="flex flex-wrap items-baseline justify-between gap-2 sm:gap-6 py-6 border-b border-[#050505]/15"
                data-testid={`winter-plan-${i + 1}`}
              >
                <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
                  <span className="font-oswald font-bold uppercase tracking-tight text-2xl sm:text-3xl">{p.name}</span>
                  <span className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.2em] text-[#050505]/60">{p.note}</span>
                </div>
                <span className="font-oswald font-bold uppercase tracking-tight text-2xl sm:text-4xl">{p.price}</span>
              </div>
            </FadeUp>
          ))}
          <FadeUp delay={0.2}>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#050505]/50">
              Starting rates — every site gets a walkthrough quote before signing
            </p>
          </FadeUp>
        </div>

        <div className="mt-14 flex flex-wrap items-center gap-6">
          <FadeUp delay={0.1}>
            <button
              onClick={reserve}
              className="group relative overflow-hidden bg-[#050505] px-10 py-5 font-mono text-xs uppercase tracking-[0.25em] text-[#F2F2F2]"
              data-testid="winter-cta-button"
            >
              <span className="absolute inset-0 -translate-y-full bg-[#FF3B00] transition-transform duration-300 ease-out group-hover:translate-y-0" />
              <span className="relative z-10 flex items-center gap-2 font-bold group-hover:text-[#050505] transition-colors duration-300">
                Reserve your route <ArrowUpRight className="h-4 w-4" />
              </span>
            </button>
          </FadeUp>
          <FadeUp delay={0.2}>
            <a
              href="tel:+14374533942"
              className="flex items-center gap-3 font-mono text-sm sm:text-base uppercase tracking-[0.15em] font-bold hover:bg-[#050505] hover:text-[#F2F2F2] px-4 py-3 transition-colors duration-300"
              data-testid="winter-phone-link"
            >
              <Phone className="h-4 w-4" />
              24/7 Storm line — +1 (437) 453-3942
            </a>
          </FadeUp>
        </div>
      </div>
      <SnowPiles yBack={pileBack} yFront={pileFront} />
    </section>
  );
};
